import { APP_CONFIG } from '@/constants/app';
import {
  Lock,
  FileText,
  Bot,
  Building2,
  FolderLock,
  FolderSync,
  FolderDown,
  Users,
  Mail,
  BarChart3,
  Shield,
  BookOpen,
  Sparkles,
  Zap,
  Database,
  Layers,
  Download,
  Eye,
  Archive,
  Scale,
  KeyRound,
  UserCheck,
  Clock,
  Stamp,
  Link2,
  Smartphone,
  ShieldCheck,
  MailCheck,
  Filter,
  Upload,
  GitBranch,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FeatureItem {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

interface VersionEntry {
  version: string;
  date: string;
  summary: string;
  isCurrent?: boolean;
  features: FeatureItem[];
}

const versions: VersionEntry[] = [
  {
    version: APP_CONFIG.version,
    date: 'Avril 2026',
    summary:
      "Envoi DSN plus fiable quand les bulletins ne sont pas prets, connexion Microsoft 365 simplifiee et meilleure identification des organismes.",
    isCurrent: true,
    features: [
      {
        icon: Clock,
        title: 'Envoi DSN securise',
        description:
          "Si tous les bulletins du mois ne sont pas encore prets, l'envoi de la DSN est automatiquement reporte au lendemain. Vous etes prevenu par email pour que votre declaration parte toujours dans les bonnes conditions.",
      },
      {
        icon: Mail,
        title: 'Connexion Microsoft 365 simplifiee',
        description:
          "Connectez votre boite Office 365 en un clic, sans mot de passe d'application. La connexion reste active sans intervention et peut etre retiree a tout moment depuis les parametres de l'expediteur.",
      },
      {
        icon: Database,
        title: "Identification amelioree des organismes",
        description:
          "Les organismes URSSAF, CGSS et MSA sont desormais identifies avec plus de precision dans vos declarations, pour une meilleure lisibilite des donnees DSN.",
      },
    ],
  },
  {
    version: '3.0.1',
    date: 'Mars 2026',
    summary:
      "Export ZIP des documents GED, filtrage hierarchique ameliore et corrections d'upload.",
    features: [
      {
        icon: FolderDown,
        title: 'Export ZIP des documents GED',
        description:
          "Export en masse des documents filtres en archive ZIP avec structure hierarchique (Domaine / Dossier / Employe / document). Disponible depuis la page GED globale et les vues contextuelles. Reserve aux administrateurs et proprietaires de l'organisation.",
      },
      {
        icon: Filter,
        title: 'Filtrage hierarchique complet',
        description:
          "Le filtre par domaine inclut desormais les documents de ses dossiers et employes. Le filtre par dossier inclut les documents de ses employes. L'export ZIP respecte les memes filtres que le listing.",
      },
      {
        icon: Upload,
        title: "Correction de l'upload de fichiers",
        description:
          "Correction d'un bug empechant l'upload de fichiers via le formulaire multipart. Les fichiers sont maintenant correctement traites par le serveur.",
      },
      {
        icon: GitBranch,
        title: 'Resolution automatique de la hierarchie',
        description:
          "Lors de la creation d'un document dans un dossier ou pour un employe, le domaine parent est automatiquement renseigne. Les documents sont retrouves quel que soit le niveau de filtrage.",
      },
    ],
  },
  {
    version: '3.0.0',
    date: 'Mars 2026',
    summary:
      "Interface complète de gestion des domaines et dossiers : archivage, restriction d'accès et indicateurs visuels sur toutes les pages.",
    features: [
      {
        icon: Archive,
        title: 'Archivage des domaines depuis l\'interface',
        description:
          "Bouton d'archivage/désarchivage disponible sur la liste des domaines (menu contextuel) et sur la page de détail du domaine. Badge \"Archivé\" affiché sur les cartes de domaines archivés.",
      },
      {
        icon: FolderLock,
        title: 'Archivage des dossiers',
        description:
          "Menu d'actions (trois points) sur chaque dossier permettant d'archiver ou désarchiver. Badge ambre \"Archivé\" visible dans la liste des dossiers et sur la page de détail.",
      },
      {
        icon: Lock,
        title: "Restriction d'accès aux dossiers",
        description:
          "Bouton de restriction d'accès disponible dans le menu contextuel de chaque dossier et sur la page de détail. Badge rouge \"Restreint\" affiché pour les dossiers protégés.",
      },
      {
        icon: Eye,
        title: 'Indicateurs visuels sur toutes les pages',
        description:
          "Badges colorés (ambre pour archivé, rouge pour restreint) visibles sur la liste des domaines, la page de détail domaine, les items de dossiers et la page de détail dossier.",
      },
      {
        icon: Users,
        title: 'Actions rapides sur les dossiers',
        description:
          "Nouveau menu contextuel (trois points) sur chaque dossier dans les vues liste, permettant d'archiver ou restreindre l'accès en un clic.",
      },
    ],
  },
  {
    version: '2.2.0',
    date: 'Mars 2026',
    summary:
      "Authentification a deux facteurs (TOTP + OTP par email), 2FA obligatoire pour le Coffre-Fort et organisations multi-comptes.",
    features: [
      {
        icon: Smartphone,
        title: 'Double methode 2FA : TOTP et OTP',
        description:
          "Choix entre une application d'authentification (Google Authenticator, Authy) ou un code envoye par email. Changement de methode a tout moment depuis les parametres de securite.",
      },
      {
        icon: MailCheck,
        title: 'OTP par email',
        description:
          "Code a 6 chiffres envoye par email a chaque connexion. Validite de 5 minutes, maximum 5 tentatives par code, cooldown de 60 secondes entre les envois.",
      },
      {
        icon: ShieldCheck,
        title: '2FA obligatoire pour le Coffre-Fort',
        description:
          "Les utilisateurs invites via le Coffre-Fort doivent obligatoirement configurer la 2FA (TOTP ou OTP) avant d'acceder a leurs documents. Page dediee de configuration forcee.",
      },
      {
        icon: KeyRound,
        title: 'Codes de secours',
        description:
          "10 codes de secours a usage unique generes lors de l'activation du TOTP. Format XXXX-XXXX, hashes en bcrypt. Utilisables en cas de perte de l'application d'authentification.",
      },
      {
        icon: Building2,
        title: 'Organisations multi-comptes',
        description:
          "Systeme d'organisations permettant de regrouper domaines et dossiers. Un utilisateur peut appartenir a plusieurs organisations avec des roles distincts (proprietaire, administrateur, membre, lecteur).",
      },
      {
        icon: Users,
        title: "Gestion des membres d'organisation",
        description:
          "Invitation de collaborateurs par email avec attribution de roles. Page dediee pour gerer les membres, modifier les roles et revoquer les acces. Selecteur d'organisation dans la barre laterale.",
      },
      {
        icon: Archive,
        title: 'Archivage des domaines et dossiers',
        description:
          "Possibilite d'archiver un domaine (avec cascade sur ses dossiers) ou un dossier individuel. Les elements archives sont masques par defaut et la synchronisation automatique est desactivee.",
      },
      {
        icon: FolderLock,
        title: "Restriction d'acces aux dossiers",
        description:
          "Les dossiers peuvent etre marques comme restreints : seuls les membres explicitement autorises y ont acces. Le proprietaire et les administrateurs conservent un acces complet.",
      },
      {
        icon: Shield,
        title: 'Guards de securite etendus',
        description:
          "Les guards d'acces aux domaines et dossiers supportent desormais l'acces via appartenance a une organisation, en plus de l'acces direct proprietaire.",
      },
    ],
  },
  {
    version: '2.1.0',
    date: 'Mars 2026',
    summary:
      "Lien vers la documentation en ligne et page de suivi des versions de l'application.",
    features: [
      {
        icon: BookOpen,
        title: 'Documentation accessible depuis le menu',
        description:
          "Lien direct vers docs.innov-paie.fr dans la barre latérale pour consulter la documentation complète de l'application.",
      },
      {
        icon: Sparkles,
        title: 'Page Nouveautés',
        description:
          "Page dédiée à l'historique des versions avec le détail de chaque mise à jour. Bulle de notification pour les nouvelles versions.",
      },
    ],
  },
  {
    version: '2.0.2',
    date: 'Février 2026',
    summary:
      'Documentation en ligne des fonctionnalités critiques de la plateforme.',
    features: [
      {
        icon: BookOpen,
        title: 'Site de documentation docs.innov-paie.fr',
        description:
          "Mise en ligne d'un site de documentation complet couvrant 9 fonctionnalités majeures : tableau de bord, domaines, dossiers, salariés, automatisations, GED, coffre-fort numérique, premiers pas et facturation.",
      },
      {
        icon: Lock,
        title: 'Documentation Coffre-Fort Numérique',
        description:
          'Guide du coffre-fort : modes de chiffrement Zero Knowledge et Server Side, cycle de vie, permissions, dépôt automatique, portail salarié et cadre légal.',
      },
      {
        icon: FolderSync,
        title: 'Documentation Dossiers et Domaines',
        description:
          "Guide complet de la gestion des dossiers de paie : synchronisation OpenPaye, vues grille/liste, filtres, onglets Info et Établissements, et configuration des automatisations par dossier.",
      },
      {
        icon: FileText,
        title: 'Documentation GED et Automatisations',
        description:
          "Guide d'utilisation de la Gestion Électronique des Documents et des 3 templates d'automatisation disponibles pour les bulletins, DSN et éditions.",
      },
      {
        icon: Layers,
        title: '7 fonctionnalités en préparation',
        description:
          "Pages placeholders pour les prochaines documentations : templates d'email, expéditeurs, dashboard RH, notifications, paramètres, support et facturation.",
      },
    ],
  },
  {
    version: '2.0.1',
    date: 'Février 2026',
    summary:
      "Migration des éditions vers l'API REST OpenPaye, génération multiple et stockage GED.",
    features: [
      {
        icon: Zap,
        title: 'Éditions via API REST OpenPaye',
        description:
          "La visualisation et la génération des éditions passent par l'API REST d'OpenPaye (api.openpaye.co) au lieu de l'automatisation navigateur Puppeteer. Résultat : temps de réponse nettement réduits et fiabilité accrue.",
      },
      {
        icon: Layers,
        title: 'Génération multiple (batch)',
        description:
          "Sélection de plusieurs types d'éditions et génération en lot pour un dossier (visualize-batch) ou pour plusieurs dossiers simultanément (bulk-generate) via une file d'attente Bull.",
      },
      {
        icon: Eye,
        title: 'Visualisation enrichie',
        description:
          "Affichage des données d'édition en tableau avec onglets Synthèse et Par mois. Export Excel client avec une feuille par mois. Support des périodes multi-années.",
      },
      {
        icon: Database,
        title: 'Stockage direct dans la GED',
        description:
          "Option \"Enregistrer dans la GED\" pour stocker les PDF générés directement dans la Gestion Électronique des Documents, rattachés au dossier avec les tags edition et auto-generated.",
      },
      {
        icon: Mail,
        title: "Envoi par e-mail depuis l'édition",
        description:
          "Possibilité d'envoyer les éditions générées par e-mail à plusieurs destinataires directement depuis le drawer d'éditions.",
      },
      {
        icon: Archive,
        title: 'Presets et configuration',
        description:
          "Système de presets nommés (sauvegardés en localStorage) pour réutiliser rapidement vos sélections d'éditions favorites. Plus de 60 types d'éditions supportés.",
      },
    ],
  },
  {
    version: '2.0.0',
    date: 'Janvier 2026',
    summary:
      'Lancement du Coffre-Fort Numérique avec chiffrement AES-256 et documentation du cadre légal.',
    features: [
      {
        icon: Lock,
        title: 'Chiffrement Zero Knowledge',
        description:
          "Chiffrement de bout en bout AES-256-GCM pour les coffres salariés. Clé dérivée du mot de passe via PBKDF2 (600 000 itérations). Le serveur ne stocke que les données chiffrées, jamais la clé.",
      },
      {
        icon: Shield,
        title: 'Mode Server Side pour les dossiers',
        description:
          'Chiffrement AES-256 côté serveur pour les coffres de dossiers/entreprise. Clé maître sécurisée avec permissions granulaires : 6 droits indépendants par utilisateur.',
      },
      {
        icon: KeyRound,
        title: 'Dépôt hybride RSA + AES',
        description:
          "Système de dépôt automatique via chiffrement hybride RSA-OAEP : l'automatisation peut déposer des documents dans un coffre Zero Knowledge sans connaître le mot de passe du salarié.",
      },
      {
        icon: Bot,
        title: 'Synchronisation automatique',
        description:
          'Dépôt automatique des bulletins de paie et DSN depuis les automatisations OpenPaye. Planification configurable (jour, heure, mois courant ou M-1) avec verrou distribué multi-instances.',
      },
      {
        icon: UserCheck,
        title: 'Portail salarié dédié',
        description:
          'Interface simplifiée /my-vaults pour les salariés : consultation, téléchargement et déchiffrement local des documents. Upload personnel autorisé (PDF, Word, Excel - 50 Mo max, quota 500 Mo).',
      },
      {
        icon: Users,
        title: "Invitations et gestion d'équipe",
        description:
          "Système d'invitations par e-mail avec configuration initiale (2FA, mot de passe temporaire). Génération en masse de coffres pour tous les salariés d'un dossier.",
      },
      {
        icon: Scale,
        title: 'Conformité loi El Khomri',
        description:
          "Conservation légale de 50 ans (ou jusqu'aux 75 ans du salarié). Préavis de fermeture de 3 mois. Page de conformité dédiée avec références légales (Code du travail, RGPD, eIDAS).",
      },
      {
        icon: Stamp,
        title: 'Horodatage technique',
        description:
          "Le produit conserve un horodatage technique. La qualification eIDAS et la vérification cryptographique complète ne sont pas établies dans l'état audité.",
      },
      {
        icon: Link2,
        title: "Piste d'audit cryptographique",
        description:
          "Journal d'audit chaîné par hachage SHA-256 (25 types d'actions). Vérification d'intégrité de la chaîne, export JSON/CSV, recherche plein texte.",
      },
      {
        icon: Clock,
        title: '2FA obligatoire',
        description:
          "Authentification à deux facteurs (TOTP) obligatoire pour tout accès aux coffres-forts. Guard dédié RequireTwoFactorGuard sur l'ensemble des endpoints sensibles.",
      },
      {
        icon: Download,
        title: 'Téléchargement sécurisé',
        description:
          'Double flux de téléchargement : déchiffrement côté navigateur (Zero Knowledge) ou côté serveur (Server Side). Vérification SHA-256 de chaque document.',
      },
      {
        icon: BarChart3,
        title: 'Statistiques et tableau de bord',
        description:
          "Vue détaillée par coffre : nombre de documents, espace utilisé, activité des membres, historique des dépôts. Page d'aide et FAQ intégrées.",
      },
    ],
  },
];

export function ChangelogPage() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {/* Header */}
      <div className="border-b border-neutral-200 pb-6">
        <h1 className="text-2xl font-bold text-neutral-900">
          {t('changelog.title', 'Nouveautés')}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {t(
            'changelog.subtitle',
            'Historique des versions et fonctionnalités'
          )}
        </p>
      </div>

      {/* Timeline */}
      <div className="relative space-y-8">
        {/* Ligne verticale de la timeline */}
        <div className="absolute top-0 left-[19px] hidden h-full w-px bg-neutral-200 sm:block" />

        {versions.map(release => (
          <div key={release.version} className="relative sm:pl-12">
            {/* Point de la timeline */}
            <div
              className={`absolute top-1 left-2.5 hidden h-3 w-3 rounded-full ring-4 ring-white sm:block ${
                release.isCurrent ? 'bg-brand-500' : 'bg-neutral-300'
              }`}
            />

            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
              {/* Header de version */}
              <div className="flex flex-wrap items-center gap-3 border-b border-neutral-100 px-6 py-5 sm:px-8">
                <h2 className="text-lg font-semibold text-neutral-900">
                  v{release.version}
                </h2>
                {release.isCurrent && (
                  <span className="bg-brand-50 text-brand-700 rounded-full px-3 py-0.5 text-xs font-medium">
                    {t('changelog.currentVersion', 'Version actuelle')}
                  </span>
                )}
                <span className="text-xs text-neutral-400">
                  {release.date}
                </span>
                <p className="w-full text-sm text-neutral-500">
                  {release.summary}
                </p>
              </div>

              {/* Features */}
              <div className="grid gap-4 p-6 sm:grid-cols-2 sm:p-8">
                {release.features.map(feature => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={feature.title}
                      className="flex gap-3 rounded-xl border border-neutral-100 p-4 transition-colors hover:bg-neutral-50"
                    >
                      <div className="bg-brand-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                        <Icon className="text-brand-600 h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-neutral-900">
                          {feature.title}
                        </h3>
                        <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
